import React, { useContext, useRef, useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import axios from 'axios';
import { formatDateFromInstant } from 'app/shared/util/date-utils';
import { FileUpload } from 'primereact/fileupload';
import { ToastContext } from 'app/context/ToastContext';
import { Button } from 'primereact/button';

interface ImportDataWidgetProps {
  importApi?: string;
  onFinishProcessing?: any;
  importLabel?: string;
  importFileName?: string;
  disabled?: boolean;
  acceptFormat?: string;
  automaticallyDownloadProtocol?: boolean;
}

const ImportDataWidget = ({
  importApi,
  onFinishProcessing,
  importLabel,
  importFileName,
  disabled,
  acceptFormat,
  automaticallyDownloadProtocol,
}: ImportDataWidgetProps) => {
  const { showSuccess, showError } = useContext(ToastContext);

  const [showDialog, setShowDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentTaskId, setCurrentTaskId] = useState(null);
  const [statusMessages, setStatusMessages] = useState([]);
  const fileUploadRef = useRef(null);

  const importData = file => {
    const formData = new FormData();
    formData.append('file', file);

    return axios.post(`${importApi}`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
  };

  const clearFileUpload = () => {
    if (fileUploadRef.current) {
      fileUploadRef.current.clear();
    }
  };

  const handleImportCSV = event => {
    setStatusMessages([]);
    const file = event.files[0];
    if (file) {
      setShowDialog(true);
      setIsProcessing(true);
      importData(file)
        .then(response => {
          setCurrentTaskId(response.data.taskId);

          setTimeout(() => {
            loadOtherLogs(response.data.taskId);
          }, 1000);
        })
        .catch(e => {
          console.error(e);
          showError('Chyba pri importovaní provízií', e.response?.data?.detail);
          clearFileUpload();
        });
    }
  };

  const onLastElementCame = taskId => {
    setIsProcessing(false);
    showSuccess('Koniec', 'Importovanie bolo ukončené.');
    clearFileUpload();

    if (onFinishProcessing) {
      onFinishProcessing();
    }

    if (automaticallyDownloadProtocol) {
      onDownloadProtocols(taskId);
    }
  };

  const loadOtherLogs = taskId => {
    axios
      .get(`${importApi}/${taskId}`)
      .then(response => {
        if (response.data) {
          setStatusMessages(prev => [...prev, ...response.data]);

          if (response.data.length > 0) {
            const lastElement = response.data[response.data.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            } else {
              setTimeout(() => {
                loadOtherLogs(taskId);
              }, 2000);
            }
          } else {
            const lastElement = statusMessages[statusMessages.length - 1];

            if (lastElement?.last) {
              onLastElementCame(taskId);
            }

            setTimeout(() => {
              loadOtherLogs(taskId);
            }, 5000);
          }
        }
      })
      .catch(e => {
        console.error(e);
        clearFileUpload();
        setIsProcessing(false);
      });
  };

  const formatLevel = level => {
    return <span style={{ color: level === 'ERROR' ? 'red' : level === 'WARN' ? 'orange' : 'blue' }}>{level}</span>;
  };

  const onHideProtocolDialog = () => {
    setShowDialog(false);
  };

  const onDownloadProtocols = taskId => {
    setIsProcessing(true);

    axios
      .get(`${importApi}/download/${taskId}`, {
        responseType: 'blob',
      })
      .then(response => {
        const blob = new Blob([response.data], { type: 'text/plain' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = importFileName ?? 'protocols.txt';
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch(e => {
        console.error(e);
        showError('Chyba', 'Neočakávaná chyba pri sťahovaní súboru');
      })
      .finally(() => setIsProcessing(false));
  };

  const dialogFooter = (
    <React.Fragment>
      <Button
        style={{ backgroundColor: '#e31818' }}
        disabled={isProcessing}
        label="Stiahnuť protokoly"
        onClick={() => onDownloadProtocols(currentTaskId)}
      />
    </React.Fragment>
  );

  return (
    <>
      <FileUpload
        ref={fileUploadRef}
        name="csv"
        mode="basic"
        accept={acceptFormat ?? '.csv'}
        maxFileSize={10000000}
        customUpload
        auto={true}
        disabled={disabled}
        uploadHandler={handleImportCSV}
        chooseLabel={importLabel}
        className="p-button-secondary"
      />
      {showDialog && (
        <Dialog
          footer={dialogFooter}
          visible={showDialog}
          style={{ width: '90%' }}
          header="Protokoly importu"
          onHide={onHideProtocolDialog}
        >
          <div style={{ width: '100%' }}>
            <DataTable
              stripedRows
              emptyMessage="Žiadne protokoly"
              size="small"
              lazy
              value={statusMessages}
              responsiveLayout="scroll"
              className="p-mt-4"
            >
              <Column field="level" header="Level" body={v => formatLevel(v.level)} />
              <Column field="message" header="Správa" />
              <Column field="timestamp" header="Čas" body={v => formatDateFromInstant(v.timestamp)} />
            </DataTable>
          </div>
        </Dialog>
      )}
    </>
  );
};

export default ImportDataWidget;
